import { useEffect, useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { Globe, Building2, Bot, Eye, Zap, CheckCircle2 } from 'lucide-react';

/**
 * UrlToCompanyChart_2 — "minimal editorial" variant of UrlToCompanyChart.
 * Same five-step path from URL to reviewed workforce (blog07), restyled as a
 * quiet magazine infographic: opaque white card, thin slate strokes, small
 * typography, and a single teal accent (#0AB3BF). No gradients, glows, or
 * shadows. Same data, same caption, same auto-advance/pause-on-hover behavior.
 */

const ACCENT = '#0AB3BF';
const SLATE_500 = '#64748B';
const SLATE_400 = '#94A3B8';
const SLATE_200 = '#E2E8F0';

const STEPS = [
  {
    title: 'Paste a URL',
    icon: <Globe className="w-4 h-4" />,
    detail: 'A company website or LinkedIn page is enough.',
  },
  {
    title: 'Read your public presence',
    icon: <Building2 className="w-4 h-4" />,
    detail: 'Brand voice, value props, products, personas — turned into a company profile.',
  },
  {
    title: 'Propose departments & bots',
    icon: <Bot className="w-4 h-4" />,
    detail: 'Bot roles and the processes they would run, per function.',
  },
  {
    title: 'You review',
    icon: <Eye className="w-4 h-4" />,
    detail: 'Approve, edit, or reject each proposal before anything deploys.',
  },
  {
    title: 'Bots earn autonomy',
    icon: <Zap className="w-4 h-4" />,
    detail: 'Each bot is promoted at its own pace as your approvals build confidence.',
  },
];

export const UrlToCompanyChart_2 = ({ height = 340 }: { height?: number }) => {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (!inView || paused) return;
    const t = setInterval(() => setActive((p) => (p + 1) % STEPS.length), 2200);
    return () => clearInterval(t);
  }, [inView, paused]);

  return (
    <div
      ref={ref}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      className="my-8 rounded-xl overflow-hidden border bg-white px-6 py-7 sm:px-10 sm:py-9"
      style={{ minHeight: height, borderColor: SLATE_200 }}
    >
      <div className="space-y-4">
        {STEPS.map((s, i) => {
          const isActive = i === active;
          const isDone = i < active;
          return (
            <motion.div
              key={s.title}
              animate={isActive ? { x: 4 } : { x: 0 }}
              transition={{ duration: 0.25 }}
              className="flex items-start gap-4 pb-4"
              style={{ borderBottom: i < STEPS.length - 1 ? `1px solid ${SLATE_200}` : 'none' }}
            >
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 border bg-white"
                style={{
                  borderColor: isActive || isDone ? ACCENT : SLATE_200,
                  color: isDone ? ACCENT : isActive ? ACCENT : SLATE_400,
                }}
              >
                {isDone ? <CheckCircle2 className="w-4 h-4" /> : s.icon}
              </div>
              <div className="flex-1 pt-0.5">
                <div className="flex items-baseline justify-between gap-2">
                  <span
                    className="text-label font-semibold tracking-tight"
                    style={{ color: isActive ? ACCENT : SLATE_500 }}
                  >
                    {i + 1}. {s.title}
                  </span>
                  {isActive && (
                    <span
                      className="text-label-2xs uppercase tracking-widest font-medium"
                      style={{ color: ACCENT }}
                    >
                      now
                    </span>
                  )}
                </div>
                <p className="text-label-sm leading-relaxed mt-1" style={{ color: SLATE_400 }}>
                  {s.detail}
                </p>
              </div>
            </motion.div>
          );
        })}
      </div>
      <p className="mt-5 text-label-xs leading-relaxed text-center" style={{ color: SLATE_400 }}>
        Figure 1. The path from URL to a reviewed, running workforce — five steps, and a human reviews before anything
        deploys. The loop pauses on hover.
      </p>
    </div>
  );
};
